<template>
    <!-- Offline read-only: the store refuses these writes regardless, so the
         control must not look live. aria-disabled rather than disabled so it
         stays focusable and can say why. See Global/user-action-button.vue. -->
    <button
        type="button"
        @click.prevent="handleClick"
        :aria-disabled="!isOnline ? 'true' : undefined"
        :aria-label="label"
        :aria-pressed="String(isPressed)"
        :aria-describedby="!isOnline ? offlineHintId : undefined"
        :title="!isOnline ? 'Unavailable offline' : undefined"
        :class="{ 'in-favorites': inFavorites, 'in-itinerary': inItinerary, 'is-offline': !isOnline, [`size-${size}`]: true }"
        class="ghost-user-action">
        <component :is="svgName" :size="size" />
        <span v-if="!isOnline" :id="offlineHintId" hidden>Unavailable offline</span>
    </button>
</template>

<script setup>
    // Libraries
    import { storeToRefs } from 'pinia';

    // Stores
    import { useUserStore } from '@/stores/user';
    const userStore = useUserStore();

    // Reactive twin of the store's isOffline() guard — same browser signal.
    const { isOnline } = useOnlineStatus();

    // Props
    const props = defineProps({
        itinerary: {
            type: Object,
            required: false,
            default: {},
        },
        list: {
            type: String,
            required: true,
            default: '',
        },
        item: {
            type: Object,
            required: false,
            default: {},
        },
        size: {
            type: String,
            required: false,
            default: 'small',
        },
    });
    const { itinerary, list, item, size } = toRefs(props);

    // Variables
    const { $event } = useNuxtApp();
    const { sites: userSites, itineraries: userItineraries } = storeToRefs(userStore);

    // A parent click listener (Ghost/site.vue) is re-emitted only when the
    // write can happen; with aria-disabled the native click still fires, so
    // it must not fall through to the root element.
    const emit = defineEmits(['click']);

    // Accessible name: every state is named for what the button does; offline
    // only adds a description.
    const offlineHintId = useId();
    const label = computed(() => userActionLabel(list.value, itemDisplayName(item.value)));
    const isPressed = computed(() => (list.value === 'ohcItineraries' ? inItinerary.value : inFavorites.value));

    // Computed
    const svgName = computed(() => {
        let svgName = '';
        switch (list.value) {
            case 'ohcItineraries':
                svgName = 'SvgsAdd';
                return svgName;
            default:
                svgName = 'SvgsHeart';
                return svgName;
        }
    });

    const inFavorites = computed(() => {
        let found = false;
        if (userSites?.value?.length) {
            userSites.value.forEach((site) => {
                if (site === item.value.id) {
                    found = true;
                }
            });
        }
        return found;
    });

    const inItinerary = computed(() => {
        let found = false;
        if (itinerary?.value?.sites?.length) {
            itinerary.value.sites.forEach((singleSite) => {
                if (singleSite === item.value.id) {
                    found = true;
                }
            });
        }

        if (userItineraries?.value?.length) {
            userItineraries.value.forEach((singleItinerary) => {
                const isGhostItinerary = singleItinerary.name.includes('ghost');
                const itemInGhost = isGhostItinerary && singleItinerary.sites.includes(item.value.id);
                if (isGhostItinerary && itemInGhost) {
                    found = true;
                }
            });
        }
        return found;
    });
    // Methods
    function itemIsInThisItinerary() {
        let found = false;
        if (itinerary?.value?.sites?.length) {
            itinerary.value.sites.forEach((singleSite) => {
                if (singleSite === item.value.id) {
                    found = true;
                }
            });
        }
        return found;
    }

    function openGlobalAddModal() {
        $event('openGlobalItineraryModal', item);
    }
    function updateItinerary() {
        $event('refreshItineraryList', {
            name: item?.value?.fields?.Name,
            list: list.value,
        });
    }
    function handleClick(event) {
        if (import.meta.client) {
            if (!isOnline.value) return;
            switch (list.value) {
                case 'ohcItineraries':
                    if (inItinerary.value) {
                        $event('removeFromGhost', item);
                    } else {
                        userStore.addSiteToItinerary(itinerary?.value?.name + '-ghost', item.value.id);
                        gtmAddedToItinerary(item);
                    }
                    break;

                default:
                    if (inFavorites.value) {
                        userStore.removeSite(item.value.id);
                    } else {
                        userStore.addSite(item.value.id);

                        gtmFavoritingSite(item);
                    }
                    break;
            }
            // After the store write, matching the old fall-through order.
            emit('click', event);
        }
    }

    function gtmFavoritingSite(item) {
        if (import.meta.client) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'favorited',
                site_name: item?.value?.fields?.Site || 'Name Unavailable',
                site_id: item?.value?.id || 'ID Unavailable',
            });
        }
    }
    function gtmAddedToItinerary(item) {
        if (import.meta.client) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'added_to_itinerary',
                site_name: item?.value?.fields?.Site || 'Name Unavailable',
                site_id: item?.value?.id || 'ID Unavailable',
            });
        }
    }
</script>

<style lang="scss" scoped>
    // Offline read-only. The store refuses the write regardless; this is the
    // half the user can see, so the control reads as unavailable rather than
    // broken.
    button[aria-disabled='true'] {
        opacity: 0.4;
        cursor: not-allowed;
    }

    // 34px icon, 44px hit area, layout unchanged.
    .size-small {
        padding: 5px;
        margin: -5px;
    }
</style>
